import { inject, Injectable } from '@angular/core';
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  Firestore,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  setDoc,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import type { QueryDocumentSnapshot } from '@angular/fire/firestore';
import { getDownloadURL, ref, Storage, uploadBytes } from '@angular/fire/storage';
import { AuthStore } from '../auth/auth.store';
import { Document, DocumentRequest, DocumentScope, UserRole } from '../models';

export interface DocumentUploadInput {
  name: string;
  description: string;
  file: File;
  categoryId: string;
  typeId: string;
  retentionYears: number;
  restricted: boolean;
  sensitive: boolean;
  scope?: DocumentScope;
  ownerId?: string;
}

export interface DocumentRequestInput {
  targetUserId: string;
  categoryId: string;
  typeId: string;
  title: string;
  note?: string;
  dueDate?: Date | null;
}

export type DocumentListScope = 'owned' | 'shared' | 'all';

export interface DocumentListOptions {
  scope: DocumentListScope;
  categoryId?: string | null;
  typeId?: string | null;
  status?: Document['status'] | null;
  year?: number | null;
  pageSize?: number;
  cursor?: QueryDocumentSnapshot | null;
}

export interface DocumentListPage {
  documents: Document[];
  cursor: QueryDocumentSnapshot | null;
}

@Injectable({ providedIn: 'root' })
export class DocumentService {
  private readonly listDocumentsLimit = 200;
  private readonly firestore = inject(Firestore);
  private readonly storage = inject(Storage);
  private readonly authStore = inject(AuthStore);

  private get tenantId(): string {
    const id = this.authStore.tenantId();
    if (!id) {
      throw new Error('No tenant selected');
    }
    return id;
  }

  private get userId(): string {
    const id = this.authStore.userId();
    if (!id) {
      throw new Error('No user authenticated');
    }
    return id;
  }

  private isAdminRole(role: UserRole | null): boolean {
    return role === 'hr_admin' || role === 'finance_admin' || role === 'platform_admin';
  }

  async listDocumentsPage(options: DocumentListOptions): Promise<DocumentListPage> {
    const tenantId = this.tenantId;
    const userId = this.userId;
    const role = this.authStore.userRole();
    const pageSize = Math.min(options.pageSize ?? 25, 50);
    const docsRef = collection(this.firestore, `tenants/${tenantId}/documents`);

    const filters: Array<ReturnType<typeof where>> = [];
    if (options.categoryId) {
      filters.push(where('categoryId', '==', options.categoryId));
    }
    if (options.typeId) {
      filters.push(where('typeId', '==', options.typeId));
    }
    if (options.status) {
      filters.push(where('status', '==', options.status));
    }
    if (options.year) {
      const start = new Date(options.year, 0, 1);
      const end = new Date(options.year + 1, 0, 1);
      filters.push(where('createdAt', '>=', start));
      filters.push(where('createdAt', '<', end));
    }

    const baseOrder = orderBy('createdAt', 'desc');
    const cursor = options.cursor ?? null;

    if (options.scope === 'owned') {
      const q = query(
        docsRef,
        where('ownerId', '==', userId),
        ...filters,
        baseOrder,
        ...(cursor ? [startAfter(cursor)] : []),
        limit(pageSize)
      );
      const snap = await getDocs(q);
      return {
        documents: snap.docs.map((d) => this.mapDocument(d.id, d.data())),
        cursor: snap.docs.at(-1) ?? null,
      };
    }

    if (options.scope === 'shared') {
      const q = query(
        docsRef,
        where('sharedWithUserIds', 'array-contains', userId),
        ...filters,
        baseOrder,
        ...(cursor ? [startAfter(cursor)] : []),
        limit(pageSize)
      );
      const snap = await getDocs(q);
      return {
        documents: snap.docs.map((d) => this.mapDocument(d.id, d.data())),
        cursor: snap.docs.at(-1) ?? null,
      };
    }

    if (!this.isAdminRole(role)) {
      const q = query(
        docsRef,
        where('ownerId', '==', userId),
        ...filters,
        baseOrder,
        ...(cursor ? [startAfter(cursor)] : []),
        limit(pageSize)
      );
      const snap = await getDocs(q);
      return {
        documents: snap.docs.map((d) => this.mapDocument(d.id, d.data())),
        cursor: snap.docs.at(-1) ?? null,
      };
    }

    const q = query(
      docsRef,
      ...filters,
      baseOrder,
      ...(cursor ? [startAfter(cursor)] : []),
      limit(pageSize)
    );
    const snap = await getDocs(q);
    return {
      documents: snap.docs.map((d) => this.mapDocument(d.id, d.data())),
      cursor: snap.docs.at(-1) ?? null,
    };
  }

  async uploadDocument(input: DocumentUploadInput): Promise<void> {
    const tenantId = this.tenantId;
    const userId = this.userId;
    const docId = crypto.randomUUID();
    const versionId = crypto.randomUUID();
    const timestamp = Date.now();
    const safeName = input.file.name.replace(/\s+/g, '-');
    const storagePath = `tenants/${tenantId}/documents/${docId}/${timestamp}-${safeName}`;

    const storageRef = ref(this.storage, storagePath);
    await uploadBytes(storageRef, input.file);
    const downloadUrl = await getDownloadURL(storageRef);

    const ownerId = input.ownerId ?? userId;
    const scope = input.scope ?? 'employee';

    const docRef = doc(this.firestore, `tenants/${tenantId}/documents/${docId}`);
    await setDoc(docRef, {
      name: input.name,
      description: input.description,
      categoryId: input.categoryId,
      typeId: input.typeId,
      scope,
      status: 'issued',
      retentionYears: input.retentionYears,
      restricted: input.restricted,
      sensitive: input.sensitive,
      storagePath,
      downloadUrl,
      mimeType: input.file.type,
      sizeBytes: input.file.size,
      uploadedBy: userId,
      ownerId,
      sharedWithUserIds: [],
      visibility: 'private',
      expiresAt: null,
      tags: [],
      version: 1,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    const versionRef = doc(
      this.firestore,
      `tenants/${tenantId}/documents/${docId}/versions/${versionId}`
    );
    await setDoc(versionRef, {
      version: 1,
      storagePath,
      downloadUrl,
      mimeType: input.file.type,
      sizeBytes: input.file.size,
      createdAt: serverTimestamp(),
      createdBy: userId,
    });
  }

  async listDocuments(): Promise<Document[]> {
    const tenantId = this.tenantId;
    const userId = this.userId;
    const role = this.authStore.userRole();
    const docsRef = collection(this.firestore, `tenants/${tenantId}/documents`);

    if (this.isAdminRole(role)) {
      const snap = await getDocs(
        query(docsRef, orderBy('createdAt', 'desc'), limit(this.listDocumentsLimit))
      );
      const documents = snap.docs.map((d) => this.mapDocument(d.id, d.data()));
      return documents.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    }

    const ownedSnap = await getDocs(
      query(docsRef, where('ownerId', '==', userId), limit(this.listDocumentsLimit))
    );
    const sharedSnap = await getDocs(
      query(docsRef, where('sharedWithUserIds', 'array-contains', userId), limit(this.listDocumentsLimit))
    );

    const merged = new Map<string, Document>();
    for (const d of ownedSnap.docs) {
      merged.set(d.id, this.mapDocument(d.id, d.data()));
    }
    for (const d of sharedSnap.docs) {
      merged.set(d.id, this.mapDocument(d.id, d.data()));
    }
    return Array.from(merged.values()).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }

  async shareDocument(documentId: string, userIds: string[]): Promise<void> {
    const tenantId = this.tenantId;
    const actorId = this.userId;
    const actorRole = this.authStore.userRole();
    const cleanUserIds = Array.from(new Set(userIds.filter((id) => id && id !== actorId)));
    if (!cleanUserIds.length) {
      return;
    }

    const docRef = doc(this.firestore, `tenants/${tenantId}/documents/${documentId}`);
    const snap = await getDoc(docRef);
    if (!snap.exists()) {
      throw new Error('documents/not-found');
    }
    const data = snap.data();

    // SEC-010: Verify caller is owner or admin before allowing share
    const ownerId = (data['ownerId'] as string) ?? '';
    if (ownerId !== actorId && !this.isAdminRole(actorRole)) {
      throw new Error('documents/not-authorized');
    }

    const docName = (data['name'] as string) ?? 'Document';

    await updateDoc(docRef, {
      sharedWithUserIds: arrayUnion(...cleanUserIds),
      updatedAt: serverTimestamp(),
    });

    const notificationsRef = collection(this.firestore, `tenants/${tenantId}/notifications`);
    await Promise.all(
      cleanUserIds.map((userId) =>
        addDoc(notificationsRef, {
          userId,
          type: 'document_shared',
          title: 'Document shared with you',
          body: `${docName} is now available in your documents.`,
          link: '/documents',
          read: false,
          createdAt: serverTimestamp(),
        })
      )
    );
  }

  async requestDocument(input: DocumentRequestInput): Promise<void> {
    const tenantId = this.tenantId;
    const requesterId = this.userId;
    const requestRef = collection(this.firestore, `tenants/${tenantId}/documentRequests`);
    const dueDate = input.dueDate ?? null;

    await addDoc(requestRef, {
      tenantId,
      targetUserId: input.targetUserId,
      requesterId,
      categoryId: input.categoryId,
      typeId: input.typeId,
      title: input.title,
      note: input.note ?? '',
      status: 'requested',
      dueDate,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  async listRequestsForCurrentUser(): Promise<DocumentRequest[]> {
    const tenantId = this.tenantId;
    const userId = this.userId;
    const requestsRef = collection(this.firestore, `tenants/${tenantId}/documentRequests`);
    const snap = await getDocs(query(requestsRef, where('targetUserId', '==', userId)));
    const requests = snap.docs.map((d) => this.mapRequest(d.id, d.data()));
    return requests.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }

  async listRequestsSentByCurrentUser(): Promise<DocumentRequest[]> {
    const tenantId = this.tenantId;
    const userId = this.userId;
    const requestsRef = collection(this.firestore, `tenants/${tenantId}/documentRequests`);
    const snap = await getDocs(query(requestsRef, where('requesterId', '==', userId)));
    const requests = snap.docs.map((d) => this.mapRequest(d.id, d.data()));
    return requests.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }

  async listRequestsForBoard(): Promise<DocumentRequest[]> {
    const tenantId = this.tenantId;
    const role = this.authStore.userRole();
    const requestsRef = collection(this.firestore, `tenants/${tenantId}/documentRequests`);

    if (role === 'hr_admin') {
      const snap = await getDocs(query(requestsRef, orderBy('createdAt', 'desc'), limit(200)));
      return snap.docs.map((docSnap) => this.mapRequest(docSnap.id, docSnap.data()));
    }

    const [target, requester] = await Promise.all([
      this.listRequestsForCurrentUser(),
      this.listRequestsSentByCurrentUser(),
    ]);
    const merged = new Map<string, DocumentRequest>();
    for (const request of target) {
      merged.set(request.id, request);
    }
    for (const request of requester) {
      merged.set(request.id, request);
    }
    return Array.from(merged.values()).sort(
      (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
    );
  }

  async markRequestProvided(requestId: string): Promise<void> {
    const tenantId = this.tenantId;
    const requestRef = doc(this.firestore, `tenants/${tenantId}/documentRequests/${requestId}`);
    await updateDoc(requestRef, {
      status: 'provided',
      updatedAt: serverTimestamp(),
    });
  }

  private mapDocument(id: string, data: Record<string, unknown>): Document {
    return {
      id,
      name: (data['name'] as string) ?? '',
      description: (data['description'] as string) ?? '',
      categoryId: (data['categoryId'] as string) ?? '',
      typeId: (data['typeId'] as string) ?? '',
      scope: (data['scope'] as DocumentScope) ?? 'employee',
      status: (data['status'] as Document['status']) ?? 'issued',
      retentionYears: Number(data['retentionYears'] ?? 0),
      restricted: Boolean(data['restricted']),
      sensitive: Boolean(data['sensitive']),
      storagePath: (data['storagePath'] as string) ?? '',
      downloadUrl: (data['downloadUrl'] as string | null) ?? null,
      mimeType: (data['mimeType'] as string) ?? '',
      sizeBytes: Number(data['sizeBytes'] ?? 0),
      uploadedBy: (data['uploadedBy'] as string) ?? '',
      ownerId: (data['ownerId'] as string) ?? '',
      sharedWithUserIds: (data['sharedWithUserIds'] as string[]) ?? [],
      visibility: (data['visibility'] as Document['visibility']) ?? 'private',
      expiresAt: (data['expiresAt'] as { toDate?: () => Date })?.toDate?.() ?? null,
      tags: (data['tags'] as string[]) ?? [],
      createdAt: (data['createdAt'] as { toDate?: () => Date })?.toDate?.() ?? new Date(),
      updatedAt: (data['updatedAt'] as { toDate?: () => Date })?.toDate?.() ?? null,
      version: Number(data['version'] ?? 1),
    };
  }

  private mapRequest(id: string, data: Record<string, unknown>): DocumentRequest {
    return {
      id,
      tenantId: (data['tenantId'] as string) ?? this.tenantId,
      targetUserId: (data['targetUserId'] as string) ?? '',
      requesterId: (data['requesterId'] as string) ?? '',
      categoryId: (data['categoryId'] as string) ?? '',
      typeId: (data['typeId'] as string) ?? '',
      title: (data['title'] as string) ?? 'Document request',
      note: (data['note'] as string) ?? '',
      status: (data['status'] as DocumentRequest['status']) ?? 'requested',
      dueDate: (data['dueDate'] as { toDate?: () => Date })?.toDate?.() ?? null,
      createdAt: (data['createdAt'] as { toDate?: () => Date })?.toDate?.() ?? new Date(),
      updatedAt: (data['updatedAt'] as { toDate?: () => Date })?.toDate?.() ?? null,
    };
  }
}
